import { useInView } from '@/hooks/reveal-in-view';
import NextImage from 'next/image';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useCountUp } from 'react-countup';

import type {
  ContentfulCardWithImageType,
  ContentfulFileType,
  ParsedContentNode,
} from '@/types';

import { ContentManager } from '../content-manager';
import { Image } from '../image';
import SplitTextAnimation from '../split-text-animation';

interface Props {
  cards: ContentfulCardWithImageType[];
  title: string;
  values: ContentfulCardWithImageType[];
}

interface CardProps {
  delay?: number;
  icon?: ContentfulFileType;
  title?: ParsedContentNode[];
}

interface StatsCircleProps {
  decimals: number;
  prefix?: string;
  suffix?: string;
  theme: 'blue' | 'black';
  value: number;
}

interface StatsProps {
  stats: ContentfulCardWithImageType;
}

const Card: FC<CardProps> = ({ delay, icon, title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <div
      className="earn-incremental--card"
      onMouseEnter={({ currentTarget }) => {
        currentTarget.classList.add('hover-animation-bounce');
      }}
      onAnimationEnd={({ currentTarget }) => {
        currentTarget.classList.remove('hover-animation-bounce');
      }}
      style={{
        transform: isInView ? 'none' : `translateY(20px)`,
        opacity: isInView ? 1 : 0,
        transition: `all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1) ${delay}s`,
      }}
      ref={ref}
    >
      {title && (
        <ContentManager
          content={title}
          className="earn-incremental--card--title"
        />
      )}
      {icon && (
        <Image
          src={icon as any}
          alt={icon.title}
          className="earn-incremental--card--icon"
        />
      )}
    </div>
  );
};

const StatsCircle: FC<StatsCircleProps> = ({
  decimals,
  prefix,
  suffix,
  theme,
  value,
}) => {
  const circleRef = useRef<HTMLDivElement | null>(null);
  const { start } = useCountUp({
    ref: circleRef,
    end: value,
    decimals,
    prefix,
    suffix,
    startOnMount: false,
  });
  const isInView = useInView(circleRef, { once: true, margin: "0px 0px 25% 0px" });

  useEffect(() => {
    if (isInView) start();
  }, [isInView]);

  return (
    <div
      ref={circleRef}
      className={`earn-incremental--stats--circle earn-incremental--stats--circle_${theme}`}
    />
  );
};

const StatsItem: FC<StatsProps> = ({ stats }) => {
  const ref = useRef(null);
  const valuesRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });
  const isValuesInView = useInView(valuesRef, { once: true, margin: "0px 0px 25% 0px" });

  const {
    decimals,
    revenue,
    revenuePrefix,
    revenueSuffix,
    profit,
    profitPrefix,
    profitSuffix,
    text,
  } = useMemo(() => {
    const values = stats.description?.map(({ html }) =>
      html?.match(/<.+?>(.*?)([+-]?(?:[0-9]*\.)?[0-9]+)(.*?)<.+?>/m),
    );
    const [revenueVal, profitVal] = values ?? [];

    if (!revenueVal || !profitVal) {
      return {
        decimals: 0,
        revenue: 0,
        revenuePrefix: '',
        revenueSuffix: '',
        profit: 0,
        profitPrefix: '',
        profitSuffix: '',
        text: stats.title,
      };
    }

    return {
      decimals: Math.max(
        revenueVal[2].split('.')[1]?.length ?? 0,
        profitVal[2].split('.')[1]?.length ?? 0,
      ),
      revenue: Number(revenueVal[2]),
      revenuePrefix: revenueVal[1],
      revenueSuffix: revenueVal[3],
      profit: Number(profitVal[2]),
      profitPrefix: profitVal[1],
      profitSuffix: profitVal[3],
      text: stats.title,
    };
  }, [stats]);

  return (
    <div className="earn-incremental--stats" ref={ref}>
      <ContentManager
        content={text as any}
        className="earn-incremental--stats--text"
        style={{
          transform: isInView ? 'none' : `translateY(20px)`,
          opacity: isInView ? 1 : 0,
          transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
        }}
      />
      <div
        className="earn-incremental--stats--values"
        ref={valuesRef}
        style={{
          transform: isValuesInView ? 'none' : `translateY(20px)`,
          opacity: isValuesInView ? 1 : 0,
          transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
        }}
      >
        <StatsCircle
          decimals={decimals}
          value={revenue}
          theme="black"
          prefix={revenuePrefix}
          suffix={revenueSuffix}
        />
        <svg
          width="45"
          height="16"
          viewBox="0 0 45 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.666667 8C0.666667 10.9455 3.05448 13.3333 6 13.3333C8.94552 13.3333 11.3333 10.9455 11.3333 8C11.3333 5.05448 8.94552 2.66667 6 2.66667C3.05448 2.66667 0.666667 5.05448 0.666667 8ZM44.7071 8.70711C45.0976 8.31658 45.0976 7.68342 44.7071 7.29289L38.3431 0.928932C37.9526 0.538408 37.3195 0.538408 36.9289 0.928932C36.5384 1.31946 36.5384 1.95262 36.9289 2.34315L42.5858 8L36.9289 13.6569C36.5384 14.0474 36.5384 14.6805 36.9289 15.0711C37.3195 15.4616 37.9526 15.4616 38.3431 15.0711L44.7071 8.70711ZM6 9H44V7H6V9Z"
            fill="#B4D8F3"
          />
        </svg>
        <StatsCircle
          decimals={decimals}
          value={profit}
          theme="blue"
          prefix={profitPrefix}
          suffix={profitSuffix}
        />
      </div>
    </div>
  );
};

export const EarnIncremental: FC<Props> = ({ cards, title, values }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <section className="earn-incremental" ref={ref}>
      <div className="earn-incremental--wrapper">
        <NextImage
          src="/images/mesh-2.png"
          alt=""
          width={1000}
          height={730}
          className="earn-incremental--bg"
        />

        <div className="earn-incremental--content">
          <SplitTextAnimation
            htmlElement={<h2 style={{ opacity: isInView ? 1 : 0 }}>{title}</h2>}
            isInView={isInView}
            className="earn-incremental--title"
          />

          <div className="earn-incremental--stats-wrapper">
            {values[0] && <StatsItem stats={values[0]} />}
            <div className="earn-incremental--stats-splitter" />
            {values[1] && <StatsItem stats={values[1]} />}
          </div>

          <div className="earn-incremental--cards-wrapper">
            {cards?.map((card, index) => (
              <Card
                key={index}
                icon={card.image}
                title={card.title}
                delay={0.2 * index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
